import { Injectable } from "@angular/core";
import { BehaviorSubject, delay, Observable } from "rxjs";
import { Food } from "src/interfaces/food.model";
import { Drink } from "src/interfaces/drink.model";
import { OrderService } from "./order.service";
import { DataService } from "./data.service";

@Injectable({
    providedIn: 'root'
  })

export class BasketService {
    private foodsSubject: BehaviorSubject<Food[]> = new BehaviorSubject<Food[]>([]);
    private drinksSubject: BehaviorSubject<Drink[]> = new BehaviorSubject<Drink[]>([]);
    public foods: Observable<Food[]> = this.foodsSubject.asObservable();
    public drinks: Observable<Drink[]> = this.drinksSubject.asObservable();
    private readonly BASKET_TIMEOUT = 30 * 60 * 1000; // 30 минут в миллисекундах

    constructor(
        private orderService: OrderService,
    ) {
        // Загрузка корзины из LocalStorage
        this.loadBasketFromStorage();
    }

    /// FOODS
    ///
    fetchFoodFromBasket(food: Food): Food | null{
        // console.log('BasketService | fetchFoodFromBasket() ', food.title);
        const currentFoods = this.foodsSubject.getValue();
        const existingFood = currentFoods.find(f => f.id === food.id);
        return existingFood || null;
    }

    pushFoodToBasket(food: Food): void {
        // console.log('BasketService | pushFoodToBasket() ', food.title);
        const currentFoods = this.foodsSubject.getValue(); // Get the current list of foods in the basket
        currentFoods.push(food); // Adding guestId to the food
        this.foodsSubject.next([...currentFoods]); // Update the basket with the modified list
        this.saveBasketToStorage();
    }

    updateFoodInBasket(food: Food): void {
        // console.log('BasketService | updateFoodInBasket() ', food.title);

        const currentFoods = this.foodsSubject.getValue(); // Get the current list of foods in the basket
        const index = currentFoods.findIndex(f => f.basketId === food.basketId); // Find if the food item already exists in the basket

        if (index !== -1) {
            currentFoods[index] = food; // Update the existing food item with guestId
        } else {
            currentFoods.push(food); // Add the new food item
        }
    
        // Update the basket with the modified list
        this.foodsSubject.next([...currentFoods]);
        this.saveBasketToStorage();
    }

    removeFoodFromBasket(food: Food): void {
        // console.log('BasketService | removeFoodFromBasket() ', food.title);
        const currentFoods = this.foodsSubject.getValue();
        const index = currentFoods.findIndex(f => f.basketId === food.basketId);
        if (index !== -1) {
            currentFoods.splice(index, 1);
            this.foodsSubject.next([...currentFoods]);
            this.saveBasketToStorage();
        }
    }

    /// DRINKS
    ///
    pushDrinkToBasket(drink: Drink): void {
        // console.log('BasketService | pushDrinkToBasket() ', drink.title);
        
        const currentDrinks = this.drinksSubject.getValue(); // Get the current list of drinks in the basket
        currentDrinks.push(drink); // Adding guestId to the drink
    
        this.drinksSubject.next([...currentDrinks]); // Update the basket with the modified list
        this.saveBasketToStorage();
    }

    updateDrinkInBasket(drink: Drink): void {
        // console.log('BasketService | updateDrinkInBasket() ', drink.title);
    
        const currentDrinks = this.drinksSubject.getValue(); // Get the current list of drinks in the basket
        const index = currentDrinks.findIndex(f => f.basketId === drink.basketId); // Find if the drink item already exists in the basket
    
        if (index !== -1) {
            currentDrinks[index] = drink; // Update the existing drink item with guestId
        } else {
            currentDrinks.push(drink); // Add the new drink item
        }
    
        // Update the basket with the modified list
        this.drinksSubject.next([...currentDrinks]);
        this.saveBasketToStorage();
    }

    removeDrinkFromBasket(drink: Drink): void {
        // console.log('BasketService | removeDrinkFromBasket() ', drink.title);
        const currentDrinks = this.drinksSubject.getValue();
        const index = currentDrinks.findIndex(f => f.basketId === drink.basketId);
        if (index !== -1) {
            currentDrinks.splice(index, 1);
            this.drinksSubject.next([...currentDrinks]);
            this.saveBasketToStorage();
        }
    }

    /// Load Basket From Locale Storage
    ///
    private loadBasketFromStorage(): void {
        const savedFoods = localStorage.getItem('basket_foods');
        const savedDrinks = localStorage.getItem('basket_drinks');
        const savedTimestamp = localStorage.getItem('basket_timestamp');

        const currentTime = new Date().getTime();
        const isBasketValid = savedTimestamp && 
                             (currentTime - parseInt(savedTimestamp, 10) <= this.BASKET_TIMEOUT);

        if (isBasketValid && savedFoods && savedDrinks) {
            // console.log('BasketService | loadBasketFromStorage() | Loading valid basket');
            this.foodsSubject.next(JSON.parse(savedFoods));
            this.drinksSubject.next(JSON.parse(savedDrinks));
        } else {
            // console.log('BasketService | loadBasketFromStorage() | Clearing expired or invalid basket');
            this.clearBasket();
        }
    }

    // Сохранение корзины в LocalStorage
    private saveBasketToStorage(): void {
        const foods = this.foodsSubject.getValue();
        const drinks = this.drinksSubject.getValue();
        localStorage.setItem('basket_foods', JSON.stringify(foods));
        localStorage.setItem('basket_drinks', JSON.stringify(drinks));
        localStorage.setItem('basket_timestamp', new Date().getTime().toString());
    }

    // Очистка корзины
    clearBasket(): void {
        // console.log('BasketService | clearBasket()');
        this.foodsSubject.next([]);
        this.drinksSubject.next([]);
        localStorage.removeItem('basket_foods');
        localStorage.removeItem('basket_drinks');
        localStorage.removeItem('basket_timestamp');
    }

    // Отправка корзины в заказ
    async sendBasketToOrder(): Promise<boolean> {
        // console.log('BasketService | sendBasketToOrder()');
        const foods = this.foodsSubject.getValue();
        const drinks = this.drinksSubject.getValue();
        if (foods.length === 0 && drinks.length === 0) {
            // console.log('BasketService | sendBasketToOrder() | Basket is empty');
            return false;
        }
        try {
            await this.orderService.createOrUpdateOrder(foods, drinks);
            // helperService.showSuccess('Корзина отправлена в заказ');
            delay(1000);
            this.clearBasket();
            return true;
        } catch (error) {
            console.error('BasketService | sendBasketToOrder() | Failed to send order:', error);
            return false;
        }
    }
}